import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { ddm, ddmSv } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { navigate, PANE_TABS, sidePath, type PaneTab } from '../router.js';
import { Dialog, LoadError, Loading, RowMenu, toast } from '../ui.js';
import { DeliveryBox } from './delivery.js';
import { ProjectBriefTab } from './ProjectBrief.js';
import { ProjectFiles } from './ProjectFiles.js';
import { ProjectShoots } from './ProjectShoots.js';
import { announce, StageBar, Videos, type Change, type ProjectDetail, type ProjectSummary } from './Projects.js';

/**
 * Two projects side by side in one tab (owner 2026-11-02, for editors): each side has what an editor needs to do a good
 * job – the brief with the delivery spec on top, the videos, the files and the shoot and its report (owner 2026-11-02: not the model releases) – and
 * nothing else (no contacts, package, booking or money). Below ~900 px there is no room for two columns: the two
 * projects become a toggle at the top, and each keeps its tab while the other shows.
 */

const TX = texts(
  {
    title: 'Side by side',
    back: '← Back to the project',
    close: 'Close side by side',
    swap: 'Swap sides',
    change: 'Change project',
    openFull: 'Open the whole project',
    tabs: { brief: 'Brief', videos: 'Videos', files: 'Files', shoot: 'Shoot' } as Record<PaneTab, string>,
    tabsLabel: (name: string) => `${name}: what to show`,
    show: 'Show project',
    noVideos: 'No videos yet.',
    // the dialog
    openBeside: 'Open beside another project…',
    pickTitle: 'Open beside',
    pickSub: (name: string) => `${name} stays on the left. Pick the project to show on the right.`,
    search: 'Search project or client…',
    mine: 'My projects',
    others: 'Other running projects',
    yourTurn: 'Your turn',
    next: (d: string) => `next ${d}`,
    noMatch: 'No running project matches.',
    loading: 'Fetching the projects…',
    cancel: 'Cancel',
    moreThan: (n: number) => `${n} more – search to narrow down`
  },
  {
    title: 'Sida vid sida',
    back: '← Tillbaka till projektet',
    close: 'Stäng sida vid sida',
    swap: 'Byt plats',
    change: 'Byt projekt',
    openFull: 'Öppna hela projektet',
    tabs: { brief: 'Brief', videos: 'Videor', files: 'Filer', shoot: 'Inspelning' },
    tabsLabel: (name: string) => `${name}: vad som visas`,
    show: 'Visa projekt',
    noVideos: 'Inga videor än.',
    openBeside: 'Öppna bredvid ett annat projekt…',
    pickTitle: 'Öppna bredvid',
    pickSub: (name: string) => `${name} stannar till vänster. Välj projektet som ska visas till höger.`,
    search: 'Sök projekt eller kund…',
    mine: 'Mina projekt',
    others: 'Andra pågående projekt',
    yourTurn: 'Din tur',
    next: (d: string) => `nästa ${d}`,
    noMatch: 'Inget pågående projekt matchar.',
    loading: 'Hämtar projekten…',
    cancel: 'Avbryt',
    moreThan: (n: number) => `${n} till – sök för att hitta fler`
  }
);

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
/** A tab change keeps the place in history: Back leaves side by side, it does not step through the tabs. */
const replaceTo = (path: string) => {
  history.replaceState(null, '', path);
  dispatchEvent(new PopStateEvent('popstate'));
};

export function SideBySide(p: { api: Api; a: string; b: string; ta: PaneTab; tb: PaneTab; mineOnly?: boolean }) {
  const t = TX[useLang()];
  const [names, setNames] = useState<Record<string, string>>({});
  const [shown, setShown] = useState<'a' | 'b'>('a');
  const [changing, setChanging] = useState(false);
  const named = (id: string) => (n: string) => setNames((o) => (o[id] === n ? o : { ...o, [id]: n }));
  return (
    <div class="screen sbs">
      <div class="sbs-bar">
        <p class="sbs-crumb">
          <a class="back-link" href={`/projects/${p.a}`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${p.a}`))}>
            {t.back}
          </a>
          <strong class="sbs-title">{t.title}</strong>
        </p>
        <div class="cluster">
          <RowMenu label={t.title} items={[{ label: t.swap, run: () => navigate(sidePath(p.b, p.a, p.tb, p.ta)) }, { label: t.change, run: () => setChanging(true) }]} />
          <button type="button" class="btn btn-sm" onClick={() => navigate(`/projects/${p.a}`)}>
            {t.close} ✕
          </button>
        </div>
      </div>
      {/* narrow screens: one project at a time, picked here */}
      <div class="seg sbs-toggle" role="group" aria-label={t.show}>
        {(['a', 'b'] as const).map((k) => (
          <button type="button" class={`seg-btn${shown === k ? ' is-on' : ''}`} aria-pressed={shown === k} onClick={() => setShown(k)}>
            {names[p[k]] ?? '…'}
          </button>
        ))}
      </div>
      <div class="sbs-grid">
        <Pane key={`a-${p.a}`} api={p.api} id={p.a} tab={p.ta} shown={shown === 'a'} onName={named(p.a)} onTab={(x) => replaceTo(sidePath(p.a, p.b, x, p.tb))} />
        <Pane
          key={`b-${p.b}`}
          api={p.api}
          id={p.b}
          tab={p.tb}
          second
          shown={shown === 'b'}
          onName={named(p.b)}
          onTab={(x) => replaceTo(sidePath(p.a, p.b, p.ta, x))}
          onChange={() => setChanging(true)}
        />
      </div>
      {changing ? <OpenBeside api={p.api} from={{ id: p.a, name: names[p.a] ?? '' }} not={p.b} mineOnly={p.mineOnly} onClose={() => setChanging(false)} onPick={(id) => (setChanging(false), navigate(sidePath(p.a, id, p.ta, p.tb)))} /> : null}
    </div>
  );
}

function Pane(p: { api: Api; id: string; tab: PaneTab; second?: boolean; shown: boolean; onName: (n: string) => void; onTab: (t: PaneTab) => void; onChange?: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<ProjectDetail | null>(() => peek(p.api, `/api/projects/${p.id}`) ?? null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    p.api.get<ProjectDetail>(`/api/projects/${p.id}`).then(setV, setErr);
  }, [p.id]);
  useEffect(() => {
    if (v) p.onName(v.name);
  }, [v?.name]);
  const run = async (fn: () => Promise<Change>) => {
    try {
      const r = await fn();
      setV(r.project);
      announce(p.api, r, setV, lang);
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  const cls = `sbs-pane${p.second ? ' sbs-pane--b' : ''}${p.shown ? ' is-shown' : ''}`;
  if (err) {
    return (
      <section class={cls}>
        <LoadError error={err} />
      </section>
    );
  }
  if (!v) {
    return (
      <section class={cls}>
        <Loading />
      </section>
    );
  }
  const tabId = (x: PaneTab) => `sbs-${p.second ? 'b' : 'a'}-${x}`;
  return (
    <section class={cls} aria-label={v.name}>
      <header class="sbs-head">
        <div class="sbs-head-row">
          <div class="sbs-name">
            <p class="pj-eyebrow">{v.client.name}</p>
            <h2 class="sbs-h2" title={v.code}>
              {v.name}
            </h2>
          </div>
          <div class="sbs-acts">
            {p.onChange ? (
              <button type="button" class="btn btn-sm btn-ghost" onClick={p.onChange}>
                {t.change}
              </button>
            ) : null}
            <a class="btn btn-sm btn-ghost" href={`/projects/${v.id}`} title={t.openFull} onClick={(e) => (e.preventDefault(), navigate(`/projects/${v.id}`))}>
              {t.openFull} ↗
            </a>
          </div>
        </div>
        <StageBar stage={v.stage} compact />
        <div class="sbs-tabs" role="tablist" aria-label={t.tabsLabel(v.name)}>
          {PANE_TABS.map((x) => (
            <button type="button" role="tab" id={tabId(x)} class="tab" aria-selected={p.tab === x} aria-current={p.tab === x ? 'page' : undefined} onClick={() => p.onTab(x)}>
              {t.tabs[x]}
            </button>
          ))}
        </div>
      </header>
      <div class="sbs-body" role="tabpanel" aria-labelledby={tabId(p.tab)}>
        {p.tab === 'brief' ? (
          <>
            {/* what to deliver first: formats, channels, lengths, subtitles and the first-draft time */}
            <DeliveryBox api={p.api} v={v} onSaved={setV} />
            <ProjectBriefTab api={p.api} id={v.id} />
          </>
        ) : p.tab === 'videos' ? (
          v.videos.length ? <Videos api={p.api} v={v} run={run} /> : <p class="muted">{t.noVideos}</p>
        ) : p.tab === 'files' ? (
          <ProjectFiles api={p.api} id={v.id} />
        ) : (
          <ProjectShoots api={p.api} id={v.id} />
        )}
      </div>
    </section>
  );
}

/** One project row in the picker: the board's row, which every role with Projects may read. */
type Choice = Pick<ProjectSummary, 'id' | 'name' | 'client' | 'mine' | 'myTurn' | 'nextDeadline' | 'hidden'>;
const MAX_OTHERS = 30;

/**
 * Pick the project to show beside `from`: my own projects first (my turn, then the nearest deadline), then the other
 * running projects. On the personal link (`mineOnly`) only one's own, as on the board.
 */
export function OpenBeside(p: { api: Api; from: { id: string; name: string }; not?: string; mineOnly?: boolean; onClose: () => void; onPick?: (id: string) => void }) {
  const lang = useLang();
  const t = TX[lang];
  const scope = p.mineOnly ? 'mine' : 'all';
  const [rows, setRows] = useState<Choice[] | null>(() => peek<{ projects: Choice[] }>(p.api, `/api/crew/board?scope=${scope}`)?.projects ?? null);
  const [err, setErr] = useState<unknown>(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    p.api.get<{ projects: Choice[] }>(`/api/crew/board?scope=${scope}`).then((r) => setRows(r.projects), setErr);
  }, []);
  const pick = (id: string) => (p.onPick ? p.onPick(id) : navigate(sidePath(p.from.id, id)));
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (r: Choice) => words.every((w) => `${r.name} ${r.client.name}`.toLowerCase().includes(w));
  const left = (rows ?? []).filter((r) => r.id !== p.from.id && r.id !== p.not && (!p.mineOnly || r.mine) && hit(r));
  const byTurn = (a: Choice, b: Choice) => Number(!!b.myTurn) - Number(!!a.myTurn) || (a.nextDeadline ?? '9999').localeCompare(b.nextDeadline ?? '9999') || a.name.localeCompare(b.name, lang);
  const mine = left.filter((r) => r.mine && !r.hidden).sort(byTurn);
  const others = left.filter((r) => !r.mine || r.hidden).sort((a, b) => a.name.localeCompare(b.name, lang));
  const d = lang === 'sv' ? ddmSv : ddm;
  const row = (r: Choice) => (
    <li>
      <button type="button" class="sbs-opt" onClick={() => pick(r.id)}>
        <span class="sbs-opt-main">
          <strong>{r.name}</strong>
          <span class="small muted">
            {r.client.name}
            {r.nextDeadline ? ` · ${t.next(d(r.nextDeadline))}` : ''}
          </span>
        </span>
        {r.myTurn ? <span class="pill pill--warn">{t.yourTurn}</span> : null}
      </button>
    </li>
  );
  return (
    <Dialog
      title={t.pickTitle}
      sub={t.pickSub(p.from.name)}
      onClose={p.onClose}
      foot={
        <button type="button" class="btn" onClick={p.onClose}>
          {t.cancel}
        </button>
      }
    >
      <input type="search" class="input sbs-search" placeholder={t.search} aria-label={t.search} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
      {err ? <LoadError error={err} /> : null}
      {!rows && !err ? <p class="muted">{t.loading}</p> : null}
      {rows && !mine.length && !others.length ? <p class="muted">{t.noMatch}</p> : null}
      <div class="sbs-list">
        {mine.length ? (
          <>
            <p class="sbs-grp">{t.mine}</p>
            <ul class="sbs-opts">{mine.map(row)}</ul>
          </>
        ) : null}
        {others.length ? (
          <>
            <p class="sbs-grp">{t.others}</p>
            <ul class="sbs-opts">{others.slice(0, MAX_OTHERS).map(row)}</ul>
            {others.length > MAX_OTHERS ? <p class="small muted">{t.moreThan(others.length - MAX_OTHERS)}</p> : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

/** The menu text, for the ⋯ menus that open the dialog. */
export const openBesideLabel = (lang: 'en' | 'sv') => TX[lang].openBeside;
